// PageHeader.tsx
// Canonical page-title row: a serif Typography.Title + a muted subtitle, with
// optional right-aligned actions. Originally built here after a header-
// consistency pass found six different hand-rolled shapes across ~11 pages
// (raw `<h1>` at three different sizes, an AntD `Typography.Title` with
// ad-hoc margin overrides, one page with its own "Govern · X" eyebrow label
// applied inconsistently). Promoted to @bluerock/corenet for other consumers
// (2026-09-01), then moved back here (2026-09-10) when a corenet cleanup
// pass dropped it from the package entirely along with corenet's other thin
// AntD wrappers (see cloudctlplane commit a94aa5df8) — this is that same
// implementation, restored verbatim rather than re-invented.
//
// Unlike AppShell/PageContainer, this isn't rendered automatically — you
// render it yourself as the first thing inside your page content, same as
// you'd render any other component. It doesn't wrap children or own layout
// beyond its own row; you're still responsible for spacing between it and
// the rest of your page content (a `Flex vertical gap` wrapper, `space-y-*`,
// or your own margins).
import type { ReactNode } from "react";
import { Flex, Typography } from "antd";

const { Title, Text } = Typography;

export interface PageHeaderProps {
  /** The page's title. Usually a string, but ReactNode so a consumer can
   *  compose e.g. a title + inline status tag. */
  title: ReactNode;
  /** Muted subtitle line below the title. Omit for a title-only header. */
  subtitle?: ReactNode;
  /** Right-aligned content — typically the page's primary action button(s).
   *  Wraps alongside the title block on narrow viewports. */
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <Flex justify="space-between" align="flex-start" wrap="wrap" gap="small">
      <div>
        {/* level={1}, not the previous level={3} — this is rendered as "the first
            thing inside your page content" (see the file header), i.e. it IS each
            page's main heading, and nothing else in AppShell/the sidebar renders an
            h1 or h2. The consolidation this component grew out of preserved AntD's
            default level-3 SIZE without re-examining whether h3 was the right
            semantic LEVEL — it wasn't; a page's own title outranking nothing else on
            the page is an invalid document outline. The explicit style below keeps
            the rendered size/weight identical to the old level-3 default so this is
            a semantic-only fix, not a visual one, across every existing consumer. */}
        <Title level={1} style={{ marginBottom: 0, fontSize: 24, fontWeight: 600, lineHeight: "32px" }}>{title}</Title>
        {subtitle && <Text type="secondary">{subtitle}</Text>}
      </div>
      {actions}
    </Flex>
  );
}

export default PageHeader;
